import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { C } from '../../shared/design-tokens.js';
import { apiFetch } from '../../lib/api-client.js';
import SafetySignatureCanvas from './SafetySignatureCanvas.js';
import { useSafetyWrite } from './use-safety-write.js';
import type { SafetyMeeting } from './use-safety-read-model.js';

interface SafetyMeetingKioskProps {
  meetingId: string | number;
  onExit: () => void;
  onMeetingClosed: () => void;
}

interface MeetingSignature {
  id?: string | number;
  employee_name?: string;
  signed_at?: string;
}

interface MeetingDetailResponse {
  meeting?: SafetyMeeting;
  signatures?: MeetingSignature[];
  error?: string;
}

export default function SafetyMeetingKiosk({ meetingId, onExit, onMeetingClosed }: SafetyMeetingKioskProps) {
  const [meeting, setMeeting] = useState<SafetyMeeting | null>(null);
  const [signatures, setSignatures] = useState<MeetingSignature[]>([]);
  const [loading, setLoading] = useState(true);
  const [driverIdInput, setDriverIdInput] = useState('');
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [signatureResetKey, setSignatureResetKey] = useState(0);
  const [signSuccess, setSignSuccess] = useState<string | null>(null);
  const [showOperatorControls, setShowOperatorControls] = useState(false);
  const [closeConfirm, setCloseConfirm] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const successTimerRef = useRef<number | null>(null);

  const {
    signMeeting,
    closeMeeting,
    submitting,
    error,
    clearError,
  } = useSafetyWrite(() => {
    /* kiosk handles close success locally */
  });

  async function loadMeetingDetail(activeMeetingId: string | number) {
    const data = await apiFetch<MeetingDetailResponse>(`/api/safety-meeting/${activeMeetingId}`);
    setMeeting(data.meeting || null);
    setSignatures(Array.isArray(data.signatures) ? data.signatures : []);
  }

  function resetForNextSigner() {
    setDriverIdInput('');
    setSignatureData(null);
    setSignatureResetKey((current) => current + 1);
    window.requestAnimationFrame(() => {
      inputRef.current?.focus({ preventScroll: true });
    });
  }

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    setSignSuccess(null);
    setShowOperatorControls(false);
    setCloseConfirm(false);
    clearError();
    loadMeetingDetail(meetingId)
      .catch(() => {
        setMeeting(null);
        setSignatures([]);
      })
      .finally(() => {
        setLoading(false);
        window.requestAnimationFrame(() => {
          inputRef.current?.focus({ preventScroll: true });
        });
      });
  // clearError is stable enough for this lifecycle; omitting avoids reload churn.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) window.clearTimeout(successTimerRef.current);
    };
  }, []);

  async function handleSignMeeting() {
    if (driverIdInput.trim().length !== 7 || !signatureData) return;
    const result = await signMeeting(meetingId, driverIdInput.trim(), signatureData);
    if (!result || typeof result !== 'object') return;

    setSignSuccess(`${result.name || 'Attendance recorded'} signed in`);
    resetForNextSigner();
    try {
      await loadMeetingDetail(meetingId);
    } catch {
      setSignSuccess('Attendance recorded, but the attendee list did not refresh');
    }
    if (successTimerRef.current) window.clearTimeout(successTimerRef.current);
    successTimerRef.current = window.setTimeout(() => setSignSuccess(null), 1800);
  }

  async function handleCloseMeeting() {
    if (!closeConfirm) {
      setCloseConfirm(true);
      return;
    }
    const ok = await closeMeeting(meetingId);
    if (!ok) return;
    onMeetingClosed();
  }

  const canSubmit = driverIdInput.trim().length === 7 && !!signatureData && !submitting;
  const recentSigners = signatures.slice(-6).reverse();

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        background: 'linear-gradient(180deg, #152033 0%, #1a2332 45%, #101826 100%)',
        color: C.text,
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          minHeight: '100vh',
          maxWidth: 560,
          margin: '0 auto',
          padding: 'max(16px, env(safe-area-inset-top)) 16px max(20px, env(safe-area-inset-bottom))',
          display: 'grid',
          gap: 16,
        }}
      >
        <div
          style={{
            padding: '18px 18px 14px',
            borderRadius: 22,
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(255,255,255,0.05)',
            boxShadow: '0 18px 50px rgba(0,0,0,0.28)',
          }}
        >
          <div style={{ color: C.gold, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 8 }}>
            Safety meeting kiosk
          </div>
          <div style={{ fontSize: 28, lineHeight: 1.1, fontWeight: 700, marginBottom: 8 }}>
            {loading ? 'Loading meeting…' : `Terminal ${meeting?.terminal_number || '—'}`}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.78)', fontSize: 15, lineHeight: 1.45 }}>
            {meeting?.topic || 'Safety meeting sign-in'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
            <span style={{ fontSize: 12, borderRadius: 999, padding: '5px 10px', background: 'rgba(78,205,196,0.14)', color: C.green }}>
              {meeting?.status === 'closed' ? 'Closed' : 'Open for attendance'}
            </span>
            <span style={{ fontSize: 12, borderRadius: 999, padding: '5px 10px', background: 'rgba(255,255,255,0.08)', color: '#fff' }}>
              {signatures.length} signed
            </span>
          </div>
        </div>

        <div
          style={{
            padding: 18,
            borderRadius: 22,
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(8,13,22,0.54)',
            boxShadow: '0 14px 42px rgba(0,0,0,0.24)',
          }}
        >
          <div style={{ color: '#ffffff', fontSize: 22, fontWeight: 700, marginBottom: 6 }}>
            Sign attendance
          </div>
          <div style={{ color: 'rgba(255,255,255,0.68)', fontSize: 14, lineHeight: 1.45, marginBottom: 18 }}>
            Enter driver ID, sign clearly, then submit. The screen resets for the next person after each successful sign-in.
          </div>

          <label
            htmlFor="safety-kiosk-driver-id"
            style={{ display: 'block', fontSize: 13, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}
          >
            Driver ID
          </label>
          <input
            ref={inputRef}
            id="safety-kiosk-driver-id"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            maxLength={7}
            value={driverIdInput}
            onChange={(e) => {
              setDriverIdInput(e.target.value.replace(/\D/g, '').slice(0, 7));
              clearError();
            }}
            placeholder="7-digit driver ID"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              borderRadius: 18,
              border: '1px solid rgba(255,255,255,0.14)',
              background: 'rgba(255,255,255,0.08)',
              color: '#fff',
              padding: '18px 20px',
              fontSize: 28,
              fontWeight: 700,
              letterSpacing: '0.08em',
              marginBottom: 18,
            }}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
            <label style={{ display: 'block', fontSize: 13, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Signature
            </label>
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
              Sign inside the white box
            </span>
          </div>
          <SafetySignatureCanvas
            resetKey={signatureResetKey}
            onSignatureChange={setSignatureData}
            height={220}
            minStrokeLength={65}
          />

          {error && (
            <div style={{ marginTop: 12, color: C.red, fontSize: 14 }}>
              {error}
            </div>
          )}
          {signSuccess && (
            <div style={{ marginTop: 12, color: C.green, fontSize: 14, fontWeight: 600 }}>
              {signSuccess}
            </div>
          )}

          <button
            type="button"
            onClick={handleSignMeeting}
            disabled={!canSubmit}
            style={{
              width: '100%',
              marginTop: 16,
              minHeight: 60,
              borderRadius: 18,
              border: 'none',
              background: canSubmit ? C.gold : 'rgba(255,255,255,0.08)',
              color: canSubmit ? C.navy : 'rgba(255,255,255,0.34)',
              fontSize: 20,
              fontWeight: 800,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {submitting ? 'Submitting…' : 'Submit attendance'}
          </button>
        </div>

        <div
          style={{
            padding: 16,
            borderRadius: 20,
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.04)',
          }}
        >
          <button
            type="button"
            onClick={() => {
              setShowOperatorControls((current) => !current);
              setCloseConfirm(false);
              clearError();
            }}
            className="btn btn-secondary"
            style={{ width: '100%', minHeight: 48, fontSize: 16 }}
          >
            {showOperatorControls ? 'Hide operator controls' : 'Operator controls'}
          </button>

          {showOperatorControls && (
            <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
              <button
                type="button"
                onClick={onExit}
                className="btn btn-ghost"
                style={{ width: '100%', minHeight: 48, fontSize: 16, color: '#fff', border: '1px solid rgba(255,255,255,0.12)' }}
              >
                Exit kiosk mode
              </button>
              <button
                type="button"
                onClick={handleCloseMeeting}
                className="btn btn-primary"
                disabled={submitting}
                style={{ width: '100%', minHeight: 52, fontSize: 17, fontWeight: 800 }}
              >
                {submitting ? 'Closing…' : closeConfirm ? 'Tap again to close meeting' : 'Close meeting'}
              </button>
              <div style={{ color: 'rgba(255,255,255,0.56)', fontSize: 12, lineHeight: 1.45 }}>
                Normal kiosk close uses the authenticated manager session. Force close stays in the desktop/operator detail drawer as the recovery path.
              </div>
            </div>
          )}
        </div>

        {recentSigners.length > 0 && (
          <div
            style={{
              padding: 16,
              borderRadius: 20,
              border: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(255,255,255,0.03)',
            }}
          >
            <div style={{ color: C.gold, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>
              Recent sign-ins
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              {recentSigners.map((sig, idx) => (
                <div
                  key={sig.id || idx}
                  style={{
                    borderRadius: 14,
                    padding: '10px 12px',
                    background: 'rgba(255,255,255,0.05)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    alignItems: 'center',
                  }}
                >
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{sig.employee_name || `Signer ${idx + 1}`}</div>
                  <div style={{ fontSize: 12, color: C.textMuted }}>
                    {sig.signed_at ? String(sig.signed_at).slice(11, 16) : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
